import Controller, { DEFAULT_HEADERS, RequestArgs } from './Controller';
import { SessionObject } from '../interfaces/session';
import JSONUtils from '../helpers/json-utils';
import * as APIRoutes from '@mm/api-routes';
import { LocalStorageService } from '@mm/utils';

export const SESSION_KEY_VERSION = '16';

export default class AuthenticatedController extends Controller {
  static getSession(): SessionObject | null {
    const session = LocalStorageService.getItem(SESSION_KEY_VERSION);

    if (session === null) {
      return null;
    }

    const parsedSession = JSONUtils.safeParse<SessionObject>(session);

    if (parsedSession) {
      return parsedSession;
    }

    return null;
  }

  static storeSession(session: SessionObject) {
    const serializedSession = JSON.stringify(session);

    LocalStorageService.setItem(SESSION_KEY_VERSION, serializedSession);
  }

  static updateSession(updatePatch: Partial<SessionObject>): void {
    const oldSession = this.getSession();

    if (oldSession) {
      this.storeSession({ ...oldSession, ...updatePatch });
    }
  }

  static async clearSession() {
    const headers =
      AuthenticatedController.addAuthTokenToHeaders(DEFAULT_HEADERS);
    const session = AuthenticatedController.getSession();

    if (!session) return;

    LocalStorageService.removeItem(SESSION_KEY_VERSION);

    await super.post(APIRoutes.SESSION_LOGOUT, {}, null, headers, null, true);
  }

  static get(
    route: string,
    args: RequestArgs = null,
    headers = DEFAULT_HEADERS,
    signal: AbortSignal | null = null,
  ) {
    return Controller.get(
      route,
      args,
      AuthenticatedController.addAuthTokenToHeaders(headers),
      signal,
    );
  }

  static put(
    route: string,
    body?: any,
    args = null,
    headers = DEFAULT_HEADERS,
  ) {
    return Controller.put(
      route,
      body,
      args,
      AuthenticatedController.addAuthTokenToHeaders(headers),
    );
  }

  static post(
    route: string,
    body?: any,
    args: RequestArgs = null,
    headers = DEFAULT_HEADERS,
    signal: AbortSignal | null = null,
    withCredentials: boolean = false,
    skipSnake?: boolean,
  ) {
    return Controller.post(
      route,
      body,
      args,
      AuthenticatedController.addAuthTokenToHeaders(headers),
      signal,
      withCredentials,
      skipSnake,
    );
  }

  static patch(
    route: string,
    body?: any,
    args: RequestArgs = null,
    headers = DEFAULT_HEADERS,
  ) {
    return Controller.patch(
      route,
      body,
      args,
      AuthenticatedController.addAuthTokenToHeaders(headers),
    );
  }

  static delete(
    route: string,
    args: RequestArgs = null,
    headers = DEFAULT_HEADERS,
    body?: any,
  ) {
    return Controller.delete(
      route,
      args,
      AuthenticatedController.addAuthTokenToHeaders(headers),
      null,
      body,
    );
  }

  static deleteWithJsonResult(
    route: string,
    args: RequestArgs = null,
    headers = DEFAULT_HEADERS,
    body?: any,
  ) {
    return Controller.deleteWithJsonResult(
      route,
      args,
      AuthenticatedController.addAuthTokenToHeaders(headers),
      null,
      body,
    );
  }

  static addAuthTokenToHeaders(headers) {
    let token: string | null = null;

    const session = AuthenticatedController.getSession();
    if (session !== null) {
      token = session.authToken;
    }

    return {
      ...headers,
      Authorization: token,
    };
  }
}
